import React, { useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import moment from "moment";
import pluralize from "pluralize";
import CopyIcon from "src/img/copy.svg";
import CalendarAutomationDetailModalContent from "./CalendarAutomationDetailModalContent";
import { captionToHtmlIfNeeded, htmlToCaption } from "./RichTextEditor/htmlToCaption";
import {
    PortalCaption,
    PortalEmptyState,
    PortalKicker,
    PortalLoadingState,
    PortalPanelHeader,
    PortalSectionTitle,
    PortalSurface,
    portalColors,
} from "./BusinessPortalComponents";
import { EVENT_SOURCE, SHARED_AUDIENCE, createCalendarItemSource } from "src/scenes/Calendar/calendarData";
import { useDashboardTheme } from "./DashboardTheme";
import { getMoodDetails } from "src/constants/moods";
import {
    StandardModal,
    StandardModalSection,
    StandardModalSectionTitle,
    StandardModalText,
} from "./StandardModal";

const cardEntrance = keyframes`
    from {
        opacity: 0;
        transform: translateY(10px) scale(0.99);
    }

    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
`;

const scrollHint = keyframes`
    0%, 100% {
        transform: translateX(0);
    }

    50% {
        transform: translateX(-6px);
    }
`;

const moodPop = keyframes`
    from {
        opacity: 0;
        transform: translateY(12px) scale(0.76);
    }

    70% {
        transform: translateY(-2px) scale(1.04);
    }

    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
`;

const SectionStack = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    margin-bottom: 1rem;
`;

const OverviewGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(12.5rem, 1fr));
    gap: 0.85rem;

    @media (min-width: 1320px) {
        grid-template-columns: repeat(${p => Math.min(Math.max(p.$count || 1, 1), 5)}, minmax(0, 1fr));
    }
`;

const OverviewCardButton = styled.button`
    min-width: 0;
    max-width: 360px;
    min-height: 13.5rem;
    display: grid;
    grid-template-rows: auto minmax(0, 1fr) auto;
    gap: 0.85rem;
    padding: 1rem;
    text-align: left;
    border: 1px solid transparent;
    border-radius: 12px;
    color: ${portalColors.text};
    background:
        linear-gradient(180deg, ${portalColors.surface}, ${portalColors.surfaceSoft}) padding-box,
        ${p => p.$accent || "linear-gradient(135deg, #C9D2E7, #171A22)"} border-box;
    box-shadow: 0 14px 30px rgba(29, 42, 89, 0.08);
    box-sizing: border-box;
    cursor: pointer;
    overflow: hidden;
    position: relative;
    animation: ${cardEntrance} 0.22s ease both;
    animation-delay: ${p => p.$delay || "0s"};
    transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;

    &:before {
        content: "";
        position: absolute;
        inset: 0;
        background: ${p => p.$wash || "transparent"};
        opacity: 0.42;
        pointer-events: none;
    }

    &:hover,
    &:focus-visible {
        transform: translateY(-2px);
        box-shadow: 0 20px 42px rgba(29, 42, 89, 0.12);
        outline: none;
    }

    &:active {
        transform: translateY(0);
    }

    [data-theme="dark"] & {
        box-shadow: 0 18px 34px rgba(0, 0, 0, 0.32);
    }

    [data-theme="dark"] &:before {
        opacity: 0.18;
    }

    > * {
        position: relative;
        z-index: 1;
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
        transition: none;

        &:hover,
        &:focus-visible,
        &:active {
            transform: none;
        }
    }
`;

const CardTop = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
`;

const CardLabel = styled.div`
    color: ${portalColors.mutedText};
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    line-height: 1.2;
    text-transform: uppercase;
`;

const CardIcon = styled.div`
    width: 2.4rem;
    height: 2.4rem;
    display: inline-grid;
    place-items: center;
    flex: 0 0 auto;
    border-radius: 10px;
    background: ${p => p.$background || "rgba(23, 26, 34, 0.08)"};
    color: ${p => p.$color || portalColors.text};
    font-size: 1.35rem;
    font-weight: 800;
`;

const CardBody = styled.div`
    min-width: 0;
    display: grid;
    align-content: start;
    gap: 0.55rem;
`;

const CardTitle = styled.div`
    color: ${p => p.$color || portalColors.text};
    font-size: 1.02rem;
    font-weight: 800;
    line-height: 1.25;
    overflow-wrap: anywhere;
`;

const CardText = styled.div`
    color: ${portalColors.mutedText};
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.45;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: ${p => p.$lines || 3};
    -webkit-box-orient: vertical;
`;

const CardMetaRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.45rem;
`;

const Pill = styled.span`
    display: inline-flex;
    align-items: center;
    min-width: 0;
    max-width: 100%;
    min-height: 1.65rem;
    padding: 0.25rem 0.55rem;
    border-radius: 999px;
    background: ${p => p.$background || "rgba(23, 26, 34, 0.08)"};
    color: ${p => p.$color || portalColors.text};
    font-size: 0.72rem;
    font-weight: 800;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const PerkPill = styled(Pill)`
    [data-theme="dark"] & {
        background: rgba(255, 255, 255, 0.1);
        color: ${portalColors.text};
    }
`;

const MediaThumb = styled.div`
    width: 100%;
    height: 5.2rem;
    border-radius: 10px;
    background: ${p => p.$background || portalColors.surfaceSoft};
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        display: block;
        object-fit: cover;
    }
`;

const EmojiThumb = styled.div`
    width: 100%;
    height: 5.2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    border-radius: 10px;
    background:
        radial-gradient(circle at 50% 35%, rgba(255, 255, 255, 0.55), transparent 42%),
        ${p => p.$background || "linear-gradient(135deg, rgba(133, 81, 174, 0.18), rgba(196, 162, 255, 0.22))"};
    border: 1px solid ${portalColors.border};
    font-size: 3.25rem;
    line-height: 1;

    [data-theme="dark"] & {
        background:
            radial-gradient(circle at 50% 35%, rgba(255, 255, 255, 0.1), transparent 42%),
            ${p => p.$background || "linear-gradient(135deg, rgba(133, 81, 174, 0.28), rgba(196, 162, 255, 0.16))"};
    }
`;

const PerksScroller = styled.div`
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(14rem, 18rem);
    gap: 0.85rem;
    overflow-x: auto;
    overscroll-behavior-inline: contain;
    padding: 0.1rem 0.2rem 0.45rem;
    scroll-snap-type: x proximity;
    scrollbar-width: thin;
    animation: ${scrollHint} 0.48s ease 0.25s both;

    @media (max-width: 640px) {
        grid-auto-columns: minmax(12.5rem, 82vw);
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const PerkCardButton = styled.button`
    min-width: 0;
    min-height: 12rem;
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    gap: 0.75rem;
    padding: 0.75rem;
    border: 1px solid ${portalColors.border};
    border-radius: 12px;
    background: ${portalColors.surfaceSoft};
    color: ${portalColors.text};
    text-align: left;
    scroll-snap-align: start;
    cursor: pointer;
    transition: transform 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease;

    &:hover,
    &:focus-visible {
        transform: translateY(-2px);
        border-color: ${portalColors.borderStrong};
        box-shadow: 0 16px 32px rgba(29, 42, 89, 0.1);
        outline: none;
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;

        &:hover,
        &:focus-visible {
            transform: none;
        }
    }
`;

const PerkImage = styled.div`
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 10px;
    background: linear-gradient(135deg, #EEF2F9, #D8DEEC);
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        display: block;
        object-fit: cover;
    }
`;

const DetailGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.85rem;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const QuoteDetailLayout = styled.div`
    display: grid;
    grid-template-columns: minmax(10rem, 18rem) minmax(0, 1fr);
    gap: 1rem;
    align-items: start;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const QuotePortraitFrame = styled.div`
    width: 100%;
    aspect-ratio: 9 / 16;
    border-radius: 10px;
    border: 1px solid ${portalColors.border};
    background: linear-gradient(135deg, #EEF2F9, #D8DEEC);
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        display: block;
        object-fit: cover;
    }
`;

const QuoteDetailStack = styled.div`
    display: grid;
    gap: 0.85rem;
`;

const QuoteArticlePreview = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.85rem;
    align-items: start;
`;

const QuoteArticleImage = styled.img`
    width: 100%;
    max-height: 18rem;
    aspect-ratio: 16 / 9;
    object-fit: cover;
    display: block;
    border-radius: 8px;
    border: 1px solid ${portalColors.border};
    background: ${portalColors.surfaceSoft};
`;

const QuoteArticleTitle = styled.div`
    color: ${portalColors.text};
    font-size: 1rem;
    font-weight: 800;
    line-height: 1.35;
    overflow-wrap: anywhere;
`;

const QuoteArticleText = styled.div`
    color: ${portalColors.mutedText};
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.5;
    margin-top: 0.35rem;

    p {
        margin: 0 0 0.75rem;
    }

    ul,
    ol {
        margin: 0 0 0.75rem 1.25rem;
        padding: 0;
    }

    li {
        margin: 0.25rem 0;
    }

    strong {
        color: ${portalColors.text};
        font-weight: 800;
    }

    a {
        color: var(--calendar-selected-cell-border, #5B6EF5);
        font-weight: 700;
        overflow-wrap: anywhere;
        text-decoration: underline;
        text-underline-offset: 3px;
    }
`;

const MoodDetailPanel = styled.div`
    display: grid;
    gap: 1rem;
    justify-items: center;
    text-align: center;
`;

const MoodDetailEmoji = styled.div`
    width: 8.5rem;
    height: 8.5rem;
    display: grid;
    place-items: center;
    border-radius: 20px;
    border: 1px solid ${portalColors.border};
    background:
        radial-gradient(circle at 50% 35%, rgba(255, 255, 255, 0.58), transparent 42%),
        linear-gradient(135deg, rgba(133, 81, 174, 0.18), rgba(196, 162, 255, 0.26));
    font-size: 5.2rem;
    line-height: 1;
    animation: ${moodPop} 0.32s ease both;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const MoodDetailName = styled.div`
    color: ${portalColors.text};
    font-size: 1.35rem;
    font-weight: 900;
    line-height: 1.2;
`;

const MoodHistoryScroller = styled.div`
    width: 100%;
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(5.5rem, 1fr);
    gap: 0.6rem;
    overflow-x: auto;
    padding-bottom: 0.25rem;
`;

const MoodHistoryChip = styled.div`
    min-height: 5.75rem;
    display: grid;
    align-content: center;
    justify-items: center;
    gap: 0.35rem;
    padding: 0.65rem;
    border: 1px solid ${portalColors.border};
    border-radius: 10px;
    background: ${portalColors.surfaceSoft};
    box-sizing: border-box;
`;

const MoodHistoryEmoji = styled.div`
    font-size: 1.8rem;
    line-height: 1;
`;

const MoodHistoryLabel = styled.div`
    color: ${portalColors.text};
    font-size: 0.72rem;
    font-weight: 800;
    line-height: 1.2;
    text-align: center;
`;

const MoodHistoryDate = styled.div`
    color: ${portalColors.mutedText};
    font-size: 0.68rem;
    font-weight: 700;
    line-height: 1.2;
`;

const PerkDetailStack = styled.div`
    width: 100%;
    max-width: min(100%, 42rem);
    display: grid;
    gap: 1rem;
`;

const PerkDetailGrid = styled.div`
    width: 100%;
    max-width: min(100%, 42rem);
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.85rem;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const DetailImage = styled.img`
    width: 100%;
    height: auto;
    max-height: 20rem;
    object-fit: cover;
    display: block;
`;

const DetailImageFrame = styled.div`
    width: 100%;
    aspect-ratio: ${p => p.$aspectRatio || "auto"};
    border-radius: 8px;
    border: 1px solid ${portalColors.border};
    background: linear-gradient(135deg, #EEF2F9, #D8DEEC);
    overflow: hidden;

    ${p => p.$aspectRatio && `
        ${DetailImage} {
            height: 100%;
            max-height: none;
        }
    `}
`;

const DetailFieldImage = styled.img`
    width: 100%;
    max-height: 12rem;
    object-fit: contain;
    display: block;
    border-radius: 8px;
    border: 1px solid ${portalColors.border};
    background: ${portalColors.surfaceSoft};
`;

const DetailLink = styled.a`
    color: var(--calendar-selected-cell-border, #5B6EF5);
    font-size: 0.95rem;
    font-weight: 700;
    line-height: 1.45;
    overflow-wrap: anywhere;
    text-decoration: underline;
    text-underline-offset: 3px;

    &:hover,
    &:focus-visible {
        color: ${portalColors.text};
        outline: none;
    }
`;

const PromoCodeBox = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    max-width: 100%;
    padding: 0.75rem 0.85rem;
    border: 1px solid transparent;
    border-radius: 10px;
    background:
        linear-gradient(${portalColors.surface}, ${portalColors.surface}) padding-box,
        linear-gradient(135deg, #8551AE, #5B6EF5, #26A26B) border-box;
    color: ${portalColors.text};
    box-shadow: 0 12px 28px rgba(91, 110, 245, 0.12);
`;

const PromoCodeText = styled.span`
    min-width: 0;
    font-size: 1rem;
    font-weight: 900;
    line-height: 1.25;
    letter-spacing: 0.04em;
    overflow-wrap: anywhere;
    background: linear-gradient(135deg, #8551AE, #5B6EF5, #26A26B);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
`;

const PromoCopyButton = styled.button`
    width: 2rem;
    height: 2rem;
    display: inline-grid;
    place-items: center;
    flex: 0 0 auto;
    border: 1px solid ${portalColors.border};
    border-radius: 8px;
    background: ${portalColors.surfaceSoft};
    cursor: pointer;
    position: relative;

    img {
        width: 1rem;
        height: 1rem;
        display: block;
    }

    &:hover,
    &:focus-visible {
        border-color: ${portalColors.borderStrong};
        outline: none;
    }
`;

const CopiedTooltip = styled.span`
    position: absolute;
    left: 50%;
    bottom: calc(100% + 0.45rem);
    transform: translateX(-50%);
    padding: 0.35rem 0.55rem;
    border-radius: 6px;
    background: ${portalColors.text};
    color: ${portalColors.surface};
    font-size: 0.72rem;
    font-weight: 800;
    line-height: 1;
    white-space: nowrap;
    box-shadow: 0 8px 18px rgba(23, 26, 34, 0.18);
    pointer-events: none;
    z-index: 2;

    &:after {
        content: "";
        position: absolute;
        left: 50%;
        top: 100%;
        transform: translateX(-50%);
        border: 5px solid transparent;
        border-top-color: ${portalColors.text};
    }
`;

const getValue = (source, keys, fallback = "") => {
    for (const key of keys) {
        const value = source?.[key];
        if (value !== undefined && value !== null && value !== "") {
            return value;
        }
    }
    return fallback;
};

const stripHtml = value => String(value || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

const formatDate = value => value ? moment(value).format("MMM D, YYYY") : "";

const toTitleCase = value => String(value || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, character => character.toUpperCase());

const normalizeWebsiteUrl = value => {
    if (!value) return "";
    return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

const compactText = value => stripHtml(value).slice(0, 120);

const formatRelativeEnd = value => {
    if (!value) return "";
    const end = moment(value).endOf("day");
    if (!end.isValid()) return "";
    if (end.isBefore(moment())) return "Ended";
    return end.fromNow(true) + " left";
};

const getDashboardAudience = source => {
    if ((source?.allClientsInPoll || source?.allClientsInSurvey || source?.allClientsInNoticeboard) &&
        (source?.allEmployeesInPoll || source?.allEmployeesInSurvey || source?.allEmployeesInNoticeboard)) return SHARED_AUDIENCE.BOTH;
    if (source?.allClientsInPoll || source?.allClientsInSurvey || source?.allClientsInNoticeboard) return SHARED_AUDIENCE.CLIENTS;
    if (source?.allEmployeesInPoll || source?.allEmployeesInSurvey || source?.allEmployeesInNoticeboard) return SHARED_AUDIENCE.EMPLOYEES;
    return SHARED_AUDIENCE.EMPLOYEES;
};

const buildCalendarAutomationItem = ({ feature, uuid, title, description, audience, detail }) => ({
    id: uuid || `${feature}-${title}`,
    uuid,
    title,
    description,
    audience,
    source: createCalendarItemSource(feature, uuid),
    automationDetail: {
        ...detail,
        feature,
        sourceReferenceId: uuid,
    },
    createdByName: detail?.createdByName || detail?.companyName || "",
    status: "active",
});

const getSingularFeatureName = featureName => pluralize.singular(String(featureName || "Quotes").trim());

const getDailyFeatureTitle = featureName => `${getSingularFeatureName(featureName)} of the day`;

const getSurveyImageUrl = survey => {
    if (survey?.image?.isUsingCompanyLogo) {
        return "";
    }

    return getValue(survey, ["imageUrl", "surveyImage"]) || survey?.image?.imageUrl;
};

const moodMessages = {
    Happy: [
        "Great energy today. Notice what helped create it and carry one small part of that into tomorrow.",
        "This is a strong moment to build from. Keep making space for what is working.",
        "A positive check-in is worth recognising. Let it reinforce the habits that support you.",
    ],
    Content: [
        "Steady is valuable. This is a good day to protect the routines that keep things balanced.",
        "Contentment can be quiet progress. Keep leaning into what feels sustainable.",
        "A grounded check-in is a useful signal. Small consistent choices are doing their job.",
    ],
    Neutral: [
        "Neutral days still give useful information. A simple reset or small win can shift the tone.",
        "A balanced check-in is a good place to observe what you need next without pressure.",
        "Nothing has to be forced today. Choose one manageable action and let that be enough.",
    ],
    Sad: [
        "This looks like a slower day. Be gentle with your expectations and take one supportive step.",
        "Sad moments matter. A pause, a chat, or a lighter plan can help you move through it.",
        "You do not need to solve everything at once. Start with care, then the next small step.",
    ],
    Unhappy: [
        "This may be a signal that something needs attention. Name one thing that would make today easier.",
        "A tough check-in deserves support, not pressure. Keep the next step small and realistic.",
        "It is okay to lower the load today. Focus on what helps you feel a little steadier.",
    ],
    Tired: [
        "Energy is information. Prioritise recovery where you can and keep the must-do list honest.",
        "Tired days call for simpler plans. Rest is part of maintaining momentum.",
        "Give yourself room to recharge. One clear priority may be more useful than many half-starts.",
    ],
    Stressed: [
        "Stress can narrow the view. Pause, breathe, and pick the next practical step.",
        "A short reset can make the load feel clearer. Break the pressure into smaller pieces.",
        "This is a good time to reduce noise. Focus on what is urgent, then what is useful.",
    ],
    Sick: [
        "Health comes first today. Keep things light and give recovery the space it needs.",
        "A sick day is a clear reason to slow down. Rest where possible and ask for help if needed.",
        "Your body is asking for care. Make the next step gentle and practical.",
    ],
};

const getMoodMessage = moodType => {
    const messages = moodMessages[moodType] || ["Today's mood check-in is ready to review."];
    const seed = moment().dayOfYear() + String(moodType || "").length;
    return messages[seed % messages.length];
};

function DashboardDetailModal({ item, onClose }) {
    const { themeMode } = useDashboardTheme();
    const [copiedValue, setCopiedValue] = useState("");

    if (!item) return null;
    const isPerk = item.type === "perk";
    const isQuote = item.type === "quote";
    const isMood = item.type === "mood";
    const isCalendarAutomation = item.calendarItem;
    const DetailGridComponent = isPerk ? PerkDetailGrid : DetailGrid;

    const copyText = value => {
        if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(value);
        }
        setCopiedValue(value);
        window.setTimeout(() => {
            setCopiedValue(currentValue => currentValue === value ? "" : currentValue);
        }, 1400);
    };

    if (isCalendarAutomation) {
        return (
            <StandardModal
                isDarkMode={themeMode === "dark"}
                open={Boolean(item)}
                onClose={onClose}
                maxWidth="md"
                bodyPadding="0"
                hideCloseButton
            >
                <CalendarAutomationDetailModalContent
                    themeMode={themeMode}
                    item={item.calendarItem}
                    onClose={onClose}
                    onViewFeature={item.onViewFeature}
                />
            </StandardModal>
        );
    }

    return (
        <StandardModal
            open={Boolean(item)}
            onClose={onClose}
            widthMode={isPerk ? "medium" : "large"}
            eyebrow={item.label}
            title={item.title}
            description={item.description}
        >
            {isQuote ? (
                <QuoteDetailLayout>
                    {item.imageUrl && (
                        <QuotePortraitFrame>
                            <img src={item.imageUrl} alt={item.title} />
                        </QuotePortraitFrame>
                    )}
                    <QuoteDetailStack>
                        {item.longText && (
                            <StandardModalSection>
                                <StandardModalSectionTitle>{item.quoteDetailTitle}</StandardModalSectionTitle>
                                <StandardModalText color={portalColors.text}>{item.longText}</StandardModalText>
                            </StandardModalSection>
                        )}
                        <DetailGrid>
                            {item.details.map(detail => (
                                <StandardModalSection key={detail.label} alignContent={detail.label === "Category" ? "start" : undefined}>
                                    <StandardModalSectionTitle>{detail.label}</StandardModalSectionTitle>
                                    {detail.url ? (
                                        <DetailLink href={detail.url} target="_blank" rel="noopener noreferrer">
                                            {detail.value}
                                        </DetailLink>
                                    ) : (
                                        <StandardModalText color={portalColors.text}>{detail.value || "Not available"}</StandardModalText>
                                    )}
                                </StandardModalSection>
                            ))}
                        </DetailGrid>
                        {item.article && (
                            <StandardModalSection>
                                <StandardModalSectionTitle>Article</StandardModalSectionTitle>
                                <QuoteArticlePreview>
                                    {item.article.imageUrl && <QuoteArticleImage src={item.article.imageUrl} alt={item.article.title} />}
                                    <div>
                                        <QuoteArticleTitle>{item.article.title}</QuoteArticleTitle>
                                        {item.article.html ? (
                                            <QuoteArticleText dangerouslySetInnerHTML={{ __html: item.article.html }} />
                                        ) : item.article.text && (
                                            <QuoteArticleText>{item.article.text}</QuoteArticleText>
                                        )}
                                    </div>
                                </QuoteArticlePreview>
                            </StandardModalSection>
                        )}
                    </QuoteDetailStack>
                </QuoteDetailLayout>
            ) : isMood ? (
                <MoodDetailPanel>
                    <MoodDetailEmoji aria-hidden="true">{item.emojiPreview}</MoodDetailEmoji>
                    {item.hasMoodLoggedToday && <MoodDetailName>{item.moodName}</MoodDetailName>}
                    <StandardModalSection>
                        <StandardModalSectionTitle>Today</StandardModalSectionTitle>
                        <StandardModalText color={portalColors.text}>{item.longText}</StandardModalText>
                    </StandardModalSection>
                    <StandardModalSection>
                        <StandardModalSectionTitle>Last 7 days</StandardModalSectionTitle>
                        {item.history?.length > 0 ? (
                            <MoodHistoryScroller aria-label="Mood history for the last 7 days">
                                {item.history.map((entry, index) => (
                                    <MoodHistoryChip key={`${entry.date}-${entry.moodType}-${index}`}>
                                        <MoodHistoryEmoji aria-hidden="true">{entry.emoji}</MoodHistoryEmoji>
                                        <MoodHistoryLabel>{entry.label}</MoodHistoryLabel>
                                        <MoodHistoryDate>{entry.displayDate}</MoodHistoryDate>
                                    </MoodHistoryChip>
                                ))}
                            </MoodHistoryScroller>
                        ) : (
                            <StandardModalText color={portalColors.mutedText}>No moods have been logged in the last 7 days.</StandardModalText>
                        )}
                    </StandardModalSection>
                </MoodDetailPanel>
            ) : isPerk ? (
                <PerkDetailStack>
                    {item.imageUrl && (
                        <DetailImageFrame $aspectRatio="16 / 9">
                            <DetailImage src={item.imageUrl} alt={item.title} />
                        </DetailImageFrame>
                    )}
                    <StandardModalSection>
                        <StandardModalSectionTitle>Description</StandardModalSectionTitle>
                        <StandardModalText color={portalColors.text}>{item.longText || "Not available"}</StandardModalText>
                    </StandardModalSection>
                </PerkDetailStack>
            ) : item.imageUrl && (
                <DetailImageFrame $aspectRatio={isPerk ? "16 / 9" : "auto"}>
                    <DetailImage src={item.imageUrl} alt={item.title} />
                </DetailImageFrame>
            )}
            {!isQuote && !isMood && (
                <DetailGridComponent>
                    {item.details.map(detail => (
                        <StandardModalSection key={detail.label}>
                            <StandardModalSectionTitle>{detail.label}</StandardModalSectionTitle>
                            {detail.copyValue ? (
                                <PromoCodeBox>
                                    <PromoCodeText>{detail.value}</PromoCodeText>
                                    <PromoCopyButton type="button" onClick={() => copyText(detail.copyValue)} aria-label="Copy perk discount code">
                                        <img src={CopyIcon} alt="" />
                                        {copiedValue === detail.copyValue && <CopiedTooltip role="status">Copied</CopiedTooltip>}
                                    </PromoCopyButton>
                                </PromoCodeBox>
                            ) : detail.imageUrl ? (
                                <DetailFieldImage src={detail.imageUrl} alt={detail.label} />
                            ) : detail.url ? (
                                <DetailLink href={detail.url} target="_blank" rel="noopener noreferrer">
                                    {detail.value}
                                </DetailLink>
                            ) : (
                                <StandardModalText color={portalColors.text}>{detail.value || "Not available"}</StandardModalText>
                            )}
                        </StandardModalSection>
                    ))}
                </DetailGridComponent>
            )}
            {item.longText && !isPerk && !isQuote && !isMood && (
                <StandardModalSection>
                    <StandardModalSectionTitle>Details</StandardModalSectionTitle>
                    <StandardModalText color={portalColors.text}>{item.longText}</StandardModalText>
                </StandardModalSection>
            )}
        </StandardModal>
    );
}

function MeTimeCard({ card, index, onSelect }) {
    return (
        <OverviewCardButton
            type="button"
            onClick={() => onSelect(card)}
            $accent={card.accent}
            $wash={card.wash}
            $delay={`${index * 0.035}s`}
            aria-label={`View ${card.label}`}
        >
            <CardTop>
                <CardLabel>{card.label}</CardLabel>
                <CardIcon $background={card.iconBackground} $color={card.iconColor}>{card.icon}</CardIcon>
            </CardTop>
            <CardBody>
                {card.emojiPreview && (
                    <EmojiThumb $background={card.emojiBackground} aria-hidden="true">
                        <span>{card.emojiPreview}</span>
                    </EmojiThumb>
                )}
                {card.imageUrl && (
                    <MediaThumb>
                        <img src={card.imageUrl} alt={card.title} />
                    </MediaThumb>
                )}
                <CardTitle $color={card.iconColor}>{card.title}</CardTitle>
                <CardText $lines={card.imageUrl || card.emojiPreview ? 2 : 4}>{card.text}</CardText>
            </CardBody>
            <CardMetaRow>
                {card.meta.filter(Boolean).map(meta => (
                    <Pill key={meta} $background={card.iconBackground} $color={card.iconColor}>{meta}</Pill>
                ))}
            </CardMetaRow>
        </OverviewCardButton>
    );
}

function buildQuoteCard(quote, quoteFeatureName = "Quotes") {
    const featureName = quoteFeatureName || "Quotes";
    const singularFeatureName = getSingularFeatureName(featureName);
    const text = getValue(quote, ["quoteText", "text", "quote"]);
    const companyName = quote?.companyName || "";
    const category = quote?.companyUuid
        ? companyName
        : quote?.isFreeContent ? "General" : "Premium";
    const imageUrl = quote?.imageUrl;
    const today = moment().format("MMM D, YYYY");
    const articleHtml = quote?.article?.articleHtml || quote?.article?.articleDescription || "";
    const article = quote?.article
        ? {
            title: quote.article.title || "Article",
            imageUrl: quote.article.imageUrl,
            html: articleHtml ? captionToHtmlIfNeeded(articleHtml) : "",
            text: htmlToCaption(articleHtml || ""),
            referenceLinks: quote.article.referenceLinks || [],
        }
        : null;
    const cardText = text || (imageUrl
        ? "Open today's featured image quote."
        : `A ${featureName.toLowerCase()} item will appear here when one is available to this user.`);

    return {
        type: "quote",
        label: `Today's ${singularFeatureName}`,
        title: quote ? getDailyFeatureTitle(featureName) : `No ${singularFeatureName.toLowerCase()} available yet`,
        text: quote ? cardText : `A ${featureName.toLowerCase()} item will appear here when one is available to this user.`,
        longText: text,
        quoteDetailTitle: singularFeatureName,
        imageUrl,
        article,
        description: quote ? category : `No ${featureName.toLowerCase()} content was returned.`,
        icon: "Q",
        iconBackground: "rgba(38, 162, 107, 0.14)",
        iconColor: "#1D7D55",
        accent: "linear-gradient(135deg, #26A26B, #7DD3B0)",
        wash: "linear-gradient(135deg, rgba(38, 162, 107, 0.1), rgba(255,255,255,0))",
        meta: quote ? [today, category] : [today, "Empty"],
        details: [
            { label: "Category", value: category },
            {
                label: "Article Link",
                value: quote?.article?.referenceLinks?.[0] || "Not available",
                url: quote?.article?.referenceLinks?.[0] ? normalizeWebsiteUrl(quote.article.referenceLinks[0]) : "",
            },
        ],
    };
}

function buildMoodCard(mood, moodHistory = []) {
    const moodType = mood?.moodType || "";
    const loggedDate = mood?.date || "";
    const moodDetails = getMoodDetails(moodType);
    const moodTitle = moodDetails?.label || toTitleCase(moodType);
    const emoji = moodDetails?.emoji || "";
    const message = moodDetails ? getMoodMessage(moodDetails.label) : "Log your mood via the app to start building your check-in history.";
    const history = (moodHistory || []).slice(0, 7).map(entry => {
        const entryMoodType = entry?.moodType || "";
        const entryMoodDetails = getMoodDetails(entryMoodType);
        const entryTitle = entryMoodDetails?.label || toTitleCase(entryMoodType);
        return {
            moodType: entryMoodType,
            label: entryTitle || "Not logged",
            emoji: entryMoodDetails?.emoji || "📲",
            date: entry?.date || "",
            displayDate: entry?.date ? moment(entry.date).format("MMM D") : "",
        };
    });

    return {
        type: "mood",
        label: "Mood Tracking",
        title: moodType ? moodTitle : "No mood logged today",
        text: moodType ? message : "Log your mood via the app",
        longText: message,
        moodName: moodTitle || "No mood logged today",
        hasMoodLoggedToday: Boolean(moodType),
        history,
        description: loggedDate ? `Logged ${formatDate(loggedDate)}` : "Today's user mood check-in.",
        icon: "M",
        emojiPreview: emoji || "📲",
        emojiBackground: "linear-gradient(135deg, rgba(133, 81, 174, 0.18), rgba(196, 162, 255, 0.24))",
        iconBackground: "rgba(133, 81, 174, 0.13)",
        iconColor: "#8551AE",
        accent: "linear-gradient(135deg, #8551AE, #C4A2FF)",
        wash: "linear-gradient(135deg, rgba(133, 81, 174, 0.11), rgba(255,255,255,0))",
        meta: [loggedDate ? formatDate(loggedDate) : "Not logged today"],
        details: [
            { label: "Mood", value: moodTitle || "No mood logged today" },
            { label: "Logged", value: formatDate(loggedDate) },
        ],
    };
}

function buildNoticeCard(notice, onViewFeature, notices = []) {
    const title = getValue(notice, ["subject", "title", "heading"], "Noticeboard post");
    const text = stripHtml(getValue(notice, ["message", "description", "text", "body"]));
    const carouselImageUrl = (notice?.carousel || []).find(mediaItem => mediaItem.imageUrl)?.imageUrl;
    const imageUrl = getValue(notice, ["imageUrl", "mediaUrl", "thumbnailUrl"]) || notice?.image?.imageUrl || notice?.video?.previewImageUrl || carouselImageUrl || notice?.media?.imageUrl;
    const postType = notice?.postType || (notice?.video?.videoUrl ? "video" : notice?.carousel?.length ? "carousel" : imageUrl ? "image" : "text");
    const date = getValue(notice, ["postedDate", "scheduleDate", "date", "createdAt"]);
    const noticeUuid = getValue(notice, ["uuid", "noticeboardUuid", "id"]);
    const reactionDetails = (notice?.reactions?.emojis || []).map(reaction => ({
        emoji: reaction.unicode,
        count: reaction.count,
    }));
    const previousPosts = (notices || [])
        .filter(item => getValue(item, ["uuid", "noticeboardUuid", "id"]) !== noticeUuid)
        .slice(0, 10)
        .map(item => ({
            uuid: getValue(item, ["uuid", "noticeboardUuid", "id"]),
            title: getValue(item, ["subject", "title", "heading"], "Noticeboard post"),
            message: compactText(getValue(item, ["message", "description", "text", "body"])),
            imageUrl: getValue(item, ["imageUrl", "mediaUrl", "thumbnailUrl"]) || item?.image?.imageUrl || item?.video?.previewImageUrl || (item?.carousel || []).find(mediaItem => mediaItem.imageUrl)?.imageUrl || item?.media?.imageUrl,
            postType: item?.postType,
            image: item?.image,
            video: item?.video,
            carousel: item?.carousel,
        }));
    const calendarItem = notice ? buildCalendarAutomationItem({
        feature: EVENT_SOURCE.NOTICEBOARD,
        uuid: noticeUuid,
        title,
        description: text,
        audience: getDashboardAudience(notice),
        detail: {
            postTitle: title,
            postMessage: text,
            featureColor: "#2563EB",
            postType,
            image: imageUrl ? { imageUrl } : undefined,
            video: notice.video,
            carousel: notice.carousel,
            media: notice.media,
            attachments: notice.attachments,
            scheduleDate: date,
            postedDate: date,
            commentsEnabled: Boolean(notice.commentsEnabled || notice.allowComments),
            reactions: reactionDetails,
            reactionCount: notice.reactions?.total,
            comments: notice.comments || [],
            commentCount: notice.commentCount ?? notice.commentsCount ?? notice.totalComments,
            checkIn: notice.checkIn,
            previousPosts,
            socialAccounts: notice.socialAccounts || [],
            createdByName: notice.createdByName || notice.authorName || "",
        },
    }) : null;

    return {
        type: "notice",
        label: "Noticeboard",
        title: notice ? title : "No noticeboard post yet",
        text: notice ? (text || "Open this noticeboard post to view the full details.") : "Posts available to this user will appear here.",
        longText: text,
        imageUrl,
        calendarItem,
        onViewFeature,
        description: notice ? (date ? `Posted ${formatDate(date)}` : "Latest available noticeboard post.") : "No noticeboard content was returned.",
        icon: "N",
        iconBackground: "rgba(37, 99, 235, 0.12)",
        iconColor: "#2563EB",
        accent: "linear-gradient(135deg, #2563EB, #22C55E)",
        wash: "linear-gradient(135deg, rgba(37, 99, 235, 0.1), rgba(255,255,255,0))",
        meta: notice ? [formatDate(date), notice.isActive === false ? "Scheduled" : "Active"] : ["Empty"],
        details: [
            { label: "Status", value: notice ? (notice.isActive === false ? "Scheduled" : "Active") : "No post returned" },
            { label: "Date", value: formatDate(date) },
        ],
    };
}

function buildSurveyCard(survey, onViewFeature, surveys = []) {
    const title = getValue(survey, ["surveyName", "name", "title"], "Available survey");
    const endDate = getValue(survey, ["finishDate", "endDate", "endsAt"]);
    const type = getValue(survey, ["surveyType", "type"]);
    const questions = survey?.questions || survey?.surveyQuestions || [];
    const questionCount = getValue(survey, ["numberOfQuestions", "questionCount"], Array.isArray(questions) ? questions.length : "");
    const surveyUuid = getValue(survey, ["uuid", "surveyUuid", "id"]);
    const imageUrl = getSurveyImageUrl(survey);
    const questionLabels = Array.isArray(questions)
        ? questions.map(question => question.question || question.title || question.text || String(question))
        : [];
    const otherSurveys = (surveys || [])
        .filter(item => getValue(item, ["uuid", "surveyUuid", "id"]) !== surveyUuid)
        .slice(0, 10)
        .map(item => {
            const itemQuestions = item?.questions || item?.surveyQuestions || [];
            const itemQuestionCount = getValue(item, ["numberOfQuestions", "questionCount"], Array.isArray(itemQuestions) ? itemQuestions.length : "");
            return {
                uuid: getValue(item, ["uuid", "surveyUuid", "id"]),
                title: getValue(item, ["surveyName", "name", "title"], "Available survey"),
                endDate: getValue(item, ["finishDate", "endDate", "endsAt"]),
                questionCount: itemQuestionCount,
            };
        });
    const calendarItem = survey ? buildCalendarAutomationItem({
        feature: EVENT_SOURCE.SURVEY,
        uuid: surveyUuid,
        title,
        description: getValue(survey, ["description"], ""),
        audience: getDashboardAudience(survey),
        detail: {
            surveyName: title,
            featureColor: "#B56F1B",
            surveyType: type,
            startDate: getValue(survey, ["startDate", "startsAt"]),
            finishDate: endDate,
            recipients: getValue(survey, ["recipients", "userGroup"]),
            showIn: getValue(survey, ["showIn", "showSurveyIn"]),
            showFrequency: getValue(survey, ["showFrequency", "frequency"]),
            isAnonymous: Boolean(survey?.isAnonymous ?? survey?.anonymous),
            image: imageUrl ? { imageUrl } : undefined,
            questions: questionLabels,
            otherSurveys,
        },
    }) : null;

    return {
        type: "survey",
        label: "Surveys",
        title: survey ? title : "No survey available yet",
        text: survey ? "Review the latest available survey and its response window." : "Available surveys will appear here when they are returned for this user.",
        longText: getValue(survey, ["description"], "Survey details are available in the surveys feature."),
        imageUrl,
        calendarItem,
        onViewFeature,
        description: survey ? (endDate ? `Ends ${formatDate(endDate)}` : "Latest available survey.") : "No active survey was returned.",
        icon: "S",
        iconBackground: "rgba(217, 139, 43, 0.14)",
        iconColor: "#B56F1B",
        accent: "linear-gradient(135deg, #D98B2B, #F7C96A)",
        wash: "linear-gradient(135deg, rgba(217, 139, 43, 0.11), rgba(255,255,255,0))",
        meta: survey ? [type, endDate ? `Ends ${formatDate(endDate)}` : "", questionCount !== "" ? `${questionCount} questions` : ""] : ["Empty"],
        details: [
            { label: "Survey Type", value: type },
            { label: "End Date", value: formatDate(endDate) },
            { label: "Questions", value: questionCount },
        ],
    };
}

function buildPollCard(poll, onViewFeature, polls = []) {
    const title = getValue(poll, ["question", "pollQuestion", "pollName", "title"], "Available poll");
    const endDate = getValue(poll, ["endDate", "finishDate", "endsAt"]);
    const options = poll?.options || poll?.pollOptions || poll?.answers || [];
    const optionCount = getValue(poll, ["numberOfOptions", "optionCount"], Array.isArray(options) ? options.length : "");
    const imageUrl = getValue(poll, ["imageUrl", "pollImage"]) || poll?.image?.imageUrl;
    const pollUuid = getValue(poll, ["uuid", "pollUuid", "id"]);
    const optionLabels = Array.isArray(options)
        ? options.map(option => option.value || option.label || option.answer || option.text || String(option))
        : [];
    const voteCount = poll?.voteCount ?? poll?.votesCount ?? poll?.totalVotes ?? poll?.responseCount ?? poll?.totalResponses ?? "";
    const otherPolls = (polls || [])
        .filter(item => getValue(item, ["uuid", "pollUuid", "id"]) !== pollUuid)
        .slice(0, 10)
        .map(item => {
            const itemOptions = item?.options || item?.pollOptions || item?.answers || [];
            const itemOptionCount = getValue(item, ["numberOfOptions", "optionCount"], Array.isArray(itemOptions) ? itemOptions.length : "");
            return {
                uuid: getValue(item, ["uuid", "pollUuid", "id"]),
                title: getValue(item, ["question", "pollQuestion", "pollName", "title"], "Available poll"),
                endDate: getValue(item, ["endDate", "finishDate", "endsAt"]),
                optionCount: itemOptionCount,
                imageUrl: getValue(item, ["imageUrl", "pollImage"]) || item?.image?.imageUrl,
            };
        });
    const calendarItem = poll ? buildCalendarAutomationItem({
        feature: EVENT_SOURCE.POLL,
        uuid: pollUuid,
        title,
        description: getValue(poll, ["description"], title),
        audience: getDashboardAudience(poll),
        detail: {
            pollName: title,
            featureColor: "#BE185D",
            question: title,
            startsAt: getValue(poll, ["startDate", "startsAt"]),
            endsAt: endDate,
            closedAt: getValue(poll, ["closedAt"]),
            recipients: getValue(poll, ["recipients", "userGroup"]),
            showPollResults: Boolean(poll?.showPollResults ?? poll?.resultVisibility),
            allEmployeesInPoll: Boolean(poll?.allEmployeesInPoll),
            allClientsInPoll: Boolean(poll?.allClientsInPoll),
            image: imageUrl ? { imageUrl } : undefined,
            options: optionLabels,
            voteCount,
            otherPolls,
        },
    }) : null;

    return {
        type: "poll",
        label: "Polls",
        title: poll ? title : "No poll available yet",
        text: poll ? (formatRelativeEnd(endDate) || "Open this poll to view the available options and response window.") : "Available polls will appear here when they are returned for this user.",
        longText: getValue(poll, ["description"], "Poll details are available in the surveys and polls feature."),
        imageUrl,
        calendarItem,
        onViewFeature,
        description: poll ? (endDate ? `Ends ${formatDate(endDate)}` : "Latest available poll.") : "No active poll was returned.",
        icon: "P",
        iconBackground: "rgba(219, 39, 119, 0.12)",
        iconColor: "#BE185D",
        accent: "linear-gradient(135deg, #DB2777, #7C3AED)",
        wash: "linear-gradient(135deg, rgba(219, 39, 119, 0.1), rgba(255,255,255,0))",
        meta: poll ? [formatRelativeEnd(endDate), endDate ? `Ends ${formatDate(endDate)}` : "", optionCount !== "" ? `${optionCount} options` : ""] : ["Empty"],
        details: [
            { label: "Time Remaining", value: formatRelativeEnd(endDate) },
            { label: "End Date", value: formatDate(endDate) },
            { label: "Options", value: optionCount },
        ],
    };
}

function buildPerkDetail(perk) {
    const imageUrl = perk.image?.imageUrl;
    const promoCode = perk.promoCode;
    const promoCodeImageUrl = perk.promoCodeImageUrl;
    const websiteUrl = normalizeWebsiteUrl(perk.website);
    const discountDetail = promoCode
        ? { label: "Perk Discount Code", value: promoCode, copyValue: promoCode }
        : { label: "Perk Discount Image", imageUrl: promoCodeImageUrl };

    return {
        type: "perk",
        label: "Available Perk",
        title: perk.title,
        text: stripHtml(perk.description),
        longText: stripHtml(perk.description),
        imageUrl,
        description: perk.companyName,
        meta: [],
        details: [
            { label: "End Date", value: formatDate(perk.endDate) },
            { label: "Website", value: perk.website, url: websiteUrl },
            discountDetail,
        ],
    };
}

export function BusinessDashboardOverview({ data, loading, availability = {}, onViewFeature = () => {}, quoteFeatureName = "Quotes" }) {
    const [selectedItem, setSelectedItem] = useState(null);

    const overviewCards = useMemo(() => ([
        availability.quote ? buildQuoteCard(data.quote, quoteFeatureName) : null,
        availability.mood ? buildMoodCard(data.mood, data.moodHistory) : null,
        availability.notice ? buildNoticeCard(data.notice, onViewFeature, data.notices) : null,
        availability.survey ? buildSurveyCard(data.survey, onViewFeature, data.surveys) : null,
        availability.poll ? buildPollCard(data.poll, onViewFeature, data.polls) : null,
    ].filter(Boolean)), [data, availability, onViewFeature, quoteFeatureName]);

    const perks = data.perks || [];
    const showPerksSection = Boolean(availability.perks) || perks.length > 0;

    if (loading) {
        return (
            <SectionStack>
                <PortalLoadingState minHeight="12rem">Loading dashboard highlights...</PortalLoadingState>
            </SectionStack>
        );
    }

    if (overviewCards.length === 0 && !showPerksSection) {
        return null;
    }

    return (
        <SectionStack>
            {overviewCards.length > 0 && (
                <PortalSurface padding="1rem">
                    <PortalPanelHeader withDivider>
                        <div>
                            <PortalKicker>Personalised Highlights</PortalKicker>
                            <PortalSectionTitle>Me Time Overview</PortalSectionTitle>
                            <PortalCaption>Recent content and check-ins available to this user.</PortalCaption>
                        </div>
                    </PortalPanelHeader>
                    <OverviewGrid $count={overviewCards.length}>
                        {overviewCards.map((card, index) => (
                            <MeTimeCard key={card.type} card={card} index={index} onSelect={setSelectedItem} />
                        ))}
                    </OverviewGrid>
                </PortalSurface>
            )}

            {showPerksSection && (
                <PortalSurface padding="1rem" marginBottom="0">
                    <PortalPanelHeader withDivider>
                        <div>
                            <PortalKicker>Benefits</PortalKicker>
                            <PortalSectionTitle>Available Perks</PortalSectionTitle>
                            <PortalCaption>Explore current perks, offers, and benefits available to you.</PortalCaption>
                        </div>
                    </PortalPanelHeader>
                    {perks.length > 0 ? (
                        <PerksScroller aria-label="Available perks">
                            {perks.map(perk => {
                                const imageUrl = perk.image?.imageUrl;
                                const title = perk.title;
                                return (
                                    <PerkCardButton key={perk.uuid} type="button" onClick={() => setSelectedItem(buildPerkDetail(perk))}>
                                        <PerkImage>
                                            {imageUrl && <img src={imageUrl} alt={title} />}
                                        </PerkImage>
                                        <CardBody>
                                            <CardTitle>{title}</CardTitle>
                                            <CardMetaRow>
                                                <PerkPill>{perk.companyName}</PerkPill>
                                                {perk.endDate && <PerkPill>Ends {formatDate(perk.endDate)}</PerkPill>}
                                            </CardMetaRow>
                                        </CardBody>
                                    </PerkCardButton>
                                );
                            })}
                        </PerksScroller>
                    ) : (
                        <PortalEmptyState minHeight="8rem">
                            <PortalSectionTitle>No available perks yet</PortalSectionTitle>
                            <PortalCaption margin="0">Perks returned for this user will appear here.</PortalCaption>
                        </PortalEmptyState>
                    )}
                </PortalSurface>
            )}

            {overviewCards.length === 0 && !showPerksSection && (
                <PortalEmptyState>
                    <PortalSectionTitle>No personalised dashboard content yet</PortalSectionTitle>
                    <PortalCaption margin="0">Available quotes, mood check-ins, noticeboard posts, surveys, polls, and perks will appear here.</PortalCaption>
                </PortalEmptyState>
            )}

            <DashboardDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} />
        </SectionStack>
    );
}

export default BusinessDashboardOverview;
